import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Wrench, Clock } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";

const EMPTY = { name: "", hours_est: 0.5, labor_rate: 400, category: "", active: true };

export default function AdminServiceTasks() {
  const { user: me } = useAuth();
  const canEdit = ["platform_owner", "master_data_admin"].includes(me?.admin_role);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => {
    setLoading(true);
    const p = q ? { q } : {};
    api.get("/admin/master-data/service-tasks", { params: p })
      .then((r) => setItems(r.data || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
     
  }, [q]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (t) => {
    setEditing(t);
    setForm({
      name: t.name, hours_est: t.hours_est, labor_rate: t.labor_rate,
      category: t.category || "", active: !!t.active,
    });
    setOpen(true);
  };
  const save = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    const payload = { ...form, hours_est: Number(form.hours_est) || 0, labor_rate: Number(form.labor_rate) || 0 };
    try {
      if (editing) {
        await api.patch(`/admin/master-data/service-tasks/${editing.id}`, payload);
        toast.success("Service task updated");
      } else {
        await api.post("/admin/master-data/service-tasks", payload);
        toast.success("Service task created");
      }
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (t) => {
    if (!window.confirm(`Delete "${t.name}"?`)) return;
    try {
      await api.delete(`/admin/master-data/service-tasks/${t.id}`);
      toast.success("Task deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-5" data-testid="admin-service-tasks-root">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#e63946] mb-1">Master Data</div>
          <h1 className="text-[26px] md:text-[30px] font-bold text-[#1d3557] flex items-center gap-2">
            <Wrench size={22} className="text-[#457b9d]" /> Service Tasks
          </h1>
          <p className="text-[13px] text-[#667085] mt-0.5">
            {items.length} task template(s). Garages see these as auto-suggestions when building estimates.
          </p>
        </div>
        {canEdit && (
          <Button
            onClick={openNew}
            data-testid="admin-service-tasks-new"
            className="h-9 bg-[#e63946] hover:bg-[#d02f3c] rounded-md text-white font-semibold text-[13px] px-3"
          >
            <Plus size={14} className="mr-1.5" /> New task
          </Button>
        )}
      </div>

      <Input
        data-testid="admin-service-tasks-search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search tasks…"
        className="max-w-md h-9 rounded-md text-[13px]"
      />

      <div className="bg-white border border-[#E4E7EC] rounded-lg overflow-hidden" data-testid="admin-service-tasks-table">
        <table className="w-full text-[13px]">
          <thead className="bg-[#F7F9FC] text-[11px] uppercase tracking-wider text-[#667085]">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Task</th>
              <th className="text-left px-4 py-3 font-semibold">Category</th>
              <th className="text-right px-4 py-3 font-semibold">Hours</th>
              <th className="text-right px-4 py-3 font-semibold">Labor rate</th>
              <th className="text-left px-4 py-3 font-semibold">Status</th>
              {canEdit && <th className="text-right px-4 py-3 font-semibold">Actions</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-[#F1FAEE]">
            {!loading && items.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-12 text-center text-[13px] text-[#667085]">No service tasks configured.</td></tr>
            )}
            {items.map((t) => (
              <tr key={t.id} data-testid={`admin-service-task-row-${t.id}`} className="hover:bg-[#F7F9FC]">
                <td className="px-4 py-3">
                  <div className="text-[13px] font-semibold text-[#1d3557]">{t.name}</div>
                </td>
                <td className="px-4 py-3 text-[12px] text-[#667085]">{t.category || "—"}</td>
                <td className="px-4 py-3 text-right">
                  <span className="inline-flex items-center gap-1 font-mono text-[#1d3557]">
                    <Clock size={11} className="text-[#457b9d]" /> {t.hours_est}h
                  </span>
                </td>
                <td className="px-4 py-3 text-right font-mono text-[#e63946] font-semibold">₹{money(t.labor_rate)}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                    t.active ? "bg-[#12B76A]/10 text-[#12B76A]" : "bg-[#66708515] text-[#667085]"
                  }`}>
                    {t.active ? "Active" : "Hidden"}
                  </span>
                </td>
                {canEdit && (
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-3">
                      <button
                        onClick={() => openEdit(t)}
                        data-testid={`admin-service-task-edit-${t.id}`}
                        className="text-[12px] text-[#457b9d] hover:underline font-semibold inline-flex items-center gap-1"
                      >
                        <Pencil size={11} /> Edit
                      </button>
                      <button
                        onClick={() => remove(t)}
                        data-testid={`admin-service-task-delete-${t.id}`}
                        className="text-[12px] text-[#e63946] hover:underline font-semibold inline-flex items-center gap-1"
                      >
                        <Trash2 size={11} /> Delete
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-lg border-[#E4E7EC] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#1d3557] font-semibold">
              {editing ? "Edit service task" : "New service task"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-[#667085] font-semibold">Task name</Label>
              <Input
                data-testid="admin-service-task-name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                className="mt-1 rounded-md"
                placeholder="e.g. Clutch Replacement"
              />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-[#667085] font-semibold">Category</Label>
              <Input
                data-testid="admin-service-task-category"
                value={form.category}
                onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                className="mt-1 rounded-md"
                placeholder="e.g. Transmission"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-[#667085] font-semibold">Est. hours</Label>
                <Input
                  data-testid="admin-service-task-hours"
                  type="number"
                  step="0.25"
                  value={form.hours_est}
                  onChange={(e) => setForm((f) => ({ ...f, hours_est: e.target.value }))}
                  className="mt-1 rounded-md font-mono"
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-[#667085] font-semibold">Labor ₹</Label>
                <Input
                  data-testid="admin-service-task-rate"
                  type="number"
                  step="50"
                  value={form.labor_rate}
                  onChange={(e) => setForm((f) => ({ ...f, labor_rate: e.target.value }))}
                  className="mt-1 rounded-md font-mono"
                />
              </div>
            </div>
            <label className="flex items-center gap-2 text-[13px] text-[#1d3557] cursor-pointer">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
                data-testid="admin-service-task-active"
                className="accent-[#e63946]"
              />
              Visible to garages
            </label>
            <Button
              onClick={save}
              data-testid="admin-service-task-save"
              className="w-full h-10 bg-[#e63946] hover:bg-[#d02f3c] rounded-md text-white font-semibold"
            >
              {editing ? "Save changes" : "Create task"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
